/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateX,
  useStateXAtom,
  useStateXAtomSetter,
  useStateXSelector,
} from '@cloudio-saas/statex';
import {
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  InputAdornment,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { MultiItemIDProp } from 'componentregistry';
import TreePopup from 'components/Popup/TreePopup';
import { helpTextSelector } from 'designer/state';
import Icon from 'icons/Icon';
import { treeViewFilterAtom } from 'layout/Sidebar/atoms';
import {
  itemIdPickerOpenAtom,
  itemIdPickerPathAtom,
} from 'layout/Sidebar/panels/TreeView/state';
import { PlatformContext } from 'platform/PlatformProvider';
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
  icon: {
    color: theme.palette.primary.main,
  },
}));

function MultiItemIDProperty(
  props: PropertyEditorProps<MultiItemIDProp, string[]>,
) {
  const {
    activeItemId,
    appUid,
    def,
    mgr,
    onChange,
    pageId,
    property,
    value: itemIdsValue = [],
    isViewOnlyUser = false,
  } = props;
  const classes = useStyles();
  const [value, setValue] = useState(itemIdsValue);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });

  return (
    <>
      <FormControl
        disabled={isViewOnlyUser}
        data-testid={property}
        className={classes.root}
        classes={{ root: classes.input }}
        fullWidth
        variant="standard"
      >
        <Typography fontSize="0.9em">
          {def.label}
          {helpText ? (
            <Icon
              // className={classes.tip}
              iconName="comment-exclamation"
              data-tip={helpText}
              data-tip-delay={0}
              data-tip-at="top"
              sx={{ position: 'absolute', right: 8 }}
            />
          ) : null}
        </Typography>
        {value.map((id, index) => (
          <Entry
            isViewOnlyUser={isViewOnlyUser}
            {...props}
            // eslint-disable-next-line react/no-array-index-key
            key={`i${index}`}
            value={id ?? ''}
            onChange={(p, v = '', u) => {
              const vals = [...value];
              if (v) {
                vals[index] = v;
              } else {
                vals.splice(index, 1);
              }
              setValue(vals);
              onChange(p, vals, u);
            }}
            index={index}
          />
        ))}
        <Entry
          {...props}
          key={`last-${value.length}`}
          value=""
          onChange={(p, v, u) => {
            if (v) {
              const vals = [...value, v];
              setValue(vals);
              onChange(p, vals, u);
            }
          }}
          index={value.length}
        />
      </FormControl>
      <TreePopup appUid={appUid} pageId={pageId} />
    </>
  );
}

function Entry({
  appUid,
  activeItemId,
  def,
  mgr,
  onChange,
  pageId,
  property,
  index,
  value: itemIdValue = '',
  isViewOnlyUser = false,
}: PropertyEditorProps<MultiItemIDProp, string> & { index: number }) {
  const classes = useStyles();
  const platform = useContext(PlatformContext);
  const setFilter = useStateXAtomSetter(treeViewFilterAtom, {
    params: { treename: 'treepopup' },
  });
  // do not use the designer path, instead use some transient path
  // as this could be used in UpdateProp Action in which case we need to
  // call onChange to change the property and not update the designer path directly
  const path = ['transient', '_itemId_', activeItemId, property, `i${index}`];
  const [itemId, setItemId] = useStateX<string>(path, '');
  const [value, setValue] = useState(itemIdValue);
  const [errorMessage, setErrorMessage] = useState('');
  const [open, setOpen] = useStateXAtom(itemIdPickerOpenAtom);
  const itemIdPickerPathSetter = useStateXAtomSetter(itemIdPickerPathAtom);

  useEffect(() => {
    // reset the value in the trasient path
    setItemId('');
  }, [setItemId]);

  const isValid = useCallback(
    (val: string): boolean => {
      if (val) {
        const item = platform.getItem(appUid, pageId, val);
        if (!item?.type) {
          setErrorMessage(`Invalid Item ID [${val}]!`);
          return false;
        }
        if (def.validateItemId) {
          const msg = def.validateItemId(val, {
            appUid,
            pageId,
            itemId: activeItemId,
            mgr,
            platform,
          });
          if (msg) {
            setErrorMessage(msg);
            return false;
          }
        }
        setErrorMessage('');
        return true;
      }
      setErrorMessage('');
      return true;
    },
    [activeItemId, appUid, def, mgr, pageId, platform],
  );

  useEffect(() => {
    if (itemId !== '') {
      // user have selected some item in the popup
      if (isValid(itemId)) {
        onChange(property, itemId);
      }
      setValue(itemId);
    } else {
      setErrorMessage('');
    }
  }, [isValid, itemId, onChange, property, setValue]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    if (val === '' || isValid(val)) {
      onChange(property, val);
      setErrorMessage('');
    }
    setValue(e.target.value);
  }

  function onBlur() {
    if (itemIdValue !== value) {
      if (isValid(value)) {
        onChange(property, value);
      } else {
        onChange(property, '');
        platform.showError(errorMessage);
      }
    }
  }

  function onClick() {
    setItemId('');
    setOpen(!open);
    itemIdPickerPathSetter(path);
    setFilter(value || def.initialFilter || activeItemId);
  }

  return (
    <>
      <Input
        disabled={isViewOnlyUser}
        spellCheck={false}
        autoCorrect="off"
        fullWidth
        onFocus={(e) => {
          e.target.select();
        }}
        onBlur={onBlur}
        type="text"
        value={value ?? ''}
        onChange={handleChange}
        endAdornment={
          <InputAdornment position="end">
            <IconButton disabled={isViewOnlyUser} onClick={onClick}>
              <Icon
                disabled={isViewOnlyUser}
                iconName="search"
                className={classes.icon}
                data-tip="Pick Item ID"
              />
            </IconButton>
          </InputAdornment>
        }
      />
      <FormHelperText error>{errorMessage}</FormHelperText>
    </>
  );
}

export default memo(MultiItemIDProperty);
